import { useEffect, useState } from "react";
import { Briefcase, TrendingUp, TrendingDown, Plus, Trash2, FileDown, Printer } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function BusinessExpenseSummary({ business, incomes, month, year, onIncomesChange }) {
  const [rows, setRows] = useState(incomes && incomes.length ? incomes : [{ id: 1, name: "", amount: 0 }]);
  useEffect(() => { setRows(incomes && incomes.length ? incomes : [{ id: 1, name: "", amount: 0 }]); }, [incomes]);

  const byCat = business?.by_category || {};
  const cats = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
  const expenses = business?.expenses || 0;
  const expensesBudget = business?.expenses_budget || 0;
  const incomeTotal = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const incomeBudget = rows.reduce((s, r) => s + (r.locked ? (Number(r.amount) || 0) : (Number(r.budget) || 0)), 0);
  const net = incomeTotal - expenses;
  const netBudget = incomeBudget - expensesBudget;
  const period = `${MONTHS[(month || 1) - 1]} ${year || ""}`;

  const commit = (next) => {
    const clean = next.filter((r) => !r.locked && ((r.name || "").trim() || Number(r.amount)));
    onIncomesChange(clean);
  };
  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { id: Date.now(), name: "", amount: 0 }]);
  const delRow = (i) => { const next = rows.filter((_, idx) => idx !== i); setRows(next.length ? next : [{ id: 1, name: "", amount: 0 }]); commit(next); };

  const buildRows = () => [
    ["Business P&L", period],
    [],
    ["Income", "Amount"],
    ...rows.filter((r) => (r.name || "").trim() || Number(r.amount)).map((r) => [r.name || "Income", (Number(r.amount) || 0).toFixed(2)]),
    ["Total income", incomeTotal.toFixed(2)],
    [],
    ["Expense category", "Amount"],
    ...cats.map(([c, a]) => [c, a.toFixed(2)]),
    ["Total expenses", expenses.toFixed(2)],
    [],
    ["Net Fluid", net.toFixed(2)],
  ];

  const exportCSV = () => {
    const csv = buildRows().map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `business-pnl-${period.replace(" ", "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPDF = () => {
    const incRows = rows.filter((r) => (r.name || "").trim() || Number(r.amount)).map((r) => `<tr><td>${r.name || "Income"}</td><td style="text-align:right">${money(r.amount)}</td></tr>`).join("");
    const expRows = cats.map(([c, a]) => `<tr><td>${c}</td><td style="text-align:right">${money(a)}</td></tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Business P&L — ${period}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:40px;max-width:640px;margin:auto}
      h1{font-size:22px;margin:0} .sub{color:#666;margin:4px 0 24px;font-size:13px}
      h2{font-size:13px;text-transform:uppercase;letter-spacing:.08em;color:#888;margin:24px 0 8px}
      table{width:100%;border-collapse:collapse} td{padding:6px 0;border-bottom:1px solid #eee;font-size:14px}
      .tot td{font-weight:700;border-top:2px solid #333;border-bottom:none}
      .net{margin-top:24px;padding:16px;background:#f6f6f4;display:flex;justify-content:space-between;font-size:18px;font-weight:700}
      .pos{color:#0a7d33}.neg{color:#c0392b}</style></head><body>
      <h1>Business Profit &amp; Loss</h1><div class="sub">${period}</div>
      <h2>Income</h2><table>${incRows || '<tr><td>No income entries</td><td></td></tr>'}<tr class="tot"><td>Total income</td><td style="text-align:right">${money(incomeTotal)}</td></tr></table>
      <h2>Expenses by category</h2><table>${expRows || '<tr><td>No business expenses</td><td></td></tr>'}<tr class="tot"><td>Total expenses</td><td style="text-align:right">${money(expenses)}</td></tr></table>
      <div class="net"><span>Net Fluid</span><span class="${net >= 0 ? "pos" : "neg"}">${money(net)}</span></div>
      </body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div data-testid="snap-business-summary" className="bg-[#171208] border border-[#FFC43D]/30 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-amber-950/40">
      <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-white flex items-center gap-2">
            <span className="w-8 h-8 grid place-items-center rounded-lg bg-[#FFC43D]/15 border border-[#FFC43D]/30"><Briefcase className="w-4 h-4 text-[#FFC43D]" /></span>
            Business Expense Summary
          </h2>
          <p className="text-xs text-slate-500 mt-1">{period} · auto-populated from expenses tagged “B”</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="snap-export-csv" onClick={exportCSV} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-white/15 text-xs text-slate-200 hover:border-[#FFC43D]/50 hover:text-[#FFC43D] transition-colors"><FileDown className="w-3.5 h-3.5" /> CSV</button>
          <button data-testid="snap-export-pdf" onClick={exportPDF} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-white/15 text-xs text-slate-200 hover:border-[#FFC43D]/50 hover:text-[#FFC43D] transition-colors"><Printer className="w-3.5 h-3.5" /> PDF</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Business income (multiple entries) */}
        <div className="md:col-span-1">
          <label className="text-[10px] font-bold tracking-wider uppercase text-[#FFC43D]">Business Income</label>
          <div className="mt-2 space-y-2">
            {rows.map((r, i) => (
              r.locked ? (
                <div key={`inv-${r.id ?? "row"}-${i}`} data-testid={`snap-income-invoice-${i}`} className="flex items-center gap-1.5 bg-slate-900/40 border border-[#2EC4B6]/25 rounded px-2 h-9">
                  <span className="flex-1 min-w-0 text-sm text-slate-300 truncate">{r.name}</span>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#2EC4B6]/15 text-[#2EC4B6] border border-[#2EC4B6]/30">invoice</span>
                  <span title="Budget" className="font-mono text-sm text-slate-400 w-[64px] text-right">{money(r.amount)}</span>
                  <span title="Actual" className="font-mono text-sm text-[#FFC43D] w-[64px] text-right">{money(r.amount)}</span>
                  <span title="Budget − Actual" className="font-mono text-xs text-emerald-400 w-[58px] text-right">{money(0)}</span>
                </div>
              ) : (
                <div key={`row-${r.id ?? "row"}-${i}`} data-testid={`snap-income-row-${i}`} className="flex items-center gap-1.5">
                  <input data-testid={`snap-income-name-${i}`} value={r.name || ""} placeholder="Source name" onChange={(e) => setRow(i, { name: e.target.value })} onBlur={() => commit(rows)} className="flex-1 min-w-0 bg-slate-900/70 border border-[#FFC43D]/25 rounded px-2 h-9 text-sm text-amber-100 focus:outline-none focus:border-[#FFC43D]" />
                  <div className="flex items-center gap-1 bg-slate-900/70 border border-white/15 rounded px-1.5 h-9 focus-within:border-slate-400 w-[64px]" title="Budget">
                    <span className="text-slate-500 text-xs">$</span>
                    <input data-testid={`snap-income-budget-${i}`} type="number" value={r.budget || ""} placeholder="Bgt" onChange={(e) => setRow(i, { budget: parseFloat(e.target.value) || 0 })} onBlur={() => commit(rows)} className="w-full bg-transparent text-slate-300 font-mono text-sm focus:outline-none" />
                  </div>
                  <div className="flex items-center gap-1 bg-slate-900/70 border border-[#FFC43D]/25 rounded px-1.5 h-9 focus-within:border-[#FFC43D] w-[64px]" title="Actual">
                    <span className="text-[#FFC43D] text-xs">$</span>
                    <input data-testid={`snap-income-amount-${i}`} type="number" value={r.amount || ""} placeholder="Act" onChange={(e) => setRow(i, { amount: parseFloat(e.target.value) || 0 })} onBlur={() => commit(rows)} className="w-full bg-transparent text-amber-100 font-mono text-sm focus:outline-none" />
                  </div>
                  <span title="Budget − Actual" data-testid={`snap-income-diff-${i}`} className={`font-mono text-xs w-[58px] text-right shrink-0 ${((Number(r.budget) || 0) - (Number(r.amount) || 0)) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money((Number(r.budget) || 0) - (Number(r.amount) || 0))}</span>
                  <button data-testid={`snap-income-remove-${i}`} onClick={() => delRow(i)} className="shrink-0 text-slate-600 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              )
            ))}
            <button data-testid="snap-income-add" onClick={addRow} className="inline-flex items-center gap-1.5 text-xs text-[#FFC43D] hover:opacity-80"><Plus className="w-3.5 h-3.5" /> Add income</button>
          </div>

          <div className="mt-4 rounded-xl bg-slate-900/60 border border-white/10 p-4">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 items-center text-[11px]">
              <span></span>
              <span className="text-[9px] uppercase text-slate-600 text-right w-[70px]">Budget</span>
              <span className="text-[9px] uppercase text-slate-600 text-right w-[70px]">Actual</span>
              <span className="text-[9px] uppercase text-slate-600 text-right w-[58px]">Δ</span>
              <span className="text-slate-500">Total income</span>
              <span className="font-mono text-right text-slate-400">{money(incomeBudget)}</span>
              <span data-testid="snap-business-income-total" className="font-mono text-right text-[#FFC43D]">{money(incomeTotal)}</span>
              <span className={`font-mono text-right ${(incomeBudget - incomeTotal) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(incomeBudget - incomeTotal)}</span>
              <span className="text-slate-500 mt-1">Expenses</span>
              <span className="font-mono text-right text-slate-400 mt-1">{money(expensesBudget)}</span>
              <span data-testid="snap-business-expenses" className="font-mono text-right text-rose-400 mt-1">{money(expenses)}</span>
              <span className={`font-mono text-right mt-1 ${(expensesBudget - expenses) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(expensesBudget - expenses)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 mt-2 border-t border-white/10">
              <span className="text-sm font-semibold text-slate-200 flex items-center gap-1">
                {net >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-rose-400" />}
                Net Fluid
              </span>
              <div className="text-right">
                <span data-testid="snap-business-net" className={`font-mono text-lg font-bold ${net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(net)}</span>
                <span className="block text-[10px] text-slate-500">budget {money(netBudget)} · Δ <span data-testid="snap-business-net-diff" className={(netBudget - net) >= 0 ? "text-emerald-400" : "text-rose-400"}>{money(netBudget - net)}</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* By category */}
        <div className="md:col-span-2">
          <label className="text-[10px] font-bold tracking-wider uppercase text-slate-500">Business Expenses by Category</label>
          {cats.length === 0 ? (
            <p data-testid="snap-business-empty" className="mt-3 text-sm text-slate-500 rounded-xl bg-slate-900/40 border border-dashed border-white/10 p-6 text-center">
              No business expenses yet. Tag any expense with “B” and pick an accounting category.
            </p>
          ) : (
            <div className="mt-2 space-y-1.5">
              {cats.map(([label, amt]) => {
                const pct = expenses ? Math.round((amt / expenses) * 100) : 0;
                return (
                  <div key={label} data-testid={`snap-business-cat-${label}`} className="flex items-center gap-3">
                    <span className="text-xs text-slate-400 w-32 truncate shrink-0">{label}</span>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-[#FFC43D]/80 rounded-full" style={{ width: `${pct}%` }} /></div>
                    <span className="font-mono text-xs text-amber-200 w-20 text-right shrink-0">{money(amt)}</span>
                  </div>
                );
              })}
              <div className="flex justify-between items-center pt-2 mt-1 border-t border-white/10">
                <span className="text-xs font-semibold text-slate-300">Total business expenses</span>
                <span className="font-mono text-sm text-rose-300">{money(expenses)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
